<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {computed, ref} from "vue";
import SearchField from "@/components/forms/SearchField.vue";
import {DataTableHeader, useDisplay} from "vuetify/framework";
import SortField from "@/components/forms/SortField.vue";
import {CapacityFrameworkSummary, Pagination, PaginationFilters} from "@/types";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import IconCountTooltip from "@/components/tooltips/IconCountTooltip.vue";
import VersionChip from "@/components/chips/VersionChip.vue";
import TitleAvatar from "@/components/avatars/TitleAvatar.vue";
import NavigationTabWithChip from "@/components/tabs/NavigationTabWithChip.vue";
import {useTeam} from "@/composables/team";

defineOptions({layout: BaseLayout})

const {isGuest} = useTeam()
const {mobile} = useDisplay()

interface Props {
    title: string
    category: 'all' | 'assessed' | 'starred' | 'contributed'
    items: Pagination<CapacityFrameworkSummary>
    filters: PaginationFilters
    count: number
    assessedCount: number
    starredCount: number
    contributedCount: number
}

const props = defineProps<Props>();

const search = ref<string | null>(props.filters.search)
const sortBy = ref<string>(props.filters.sort_by)
const sortDir = ref<'asc' | 'desc'>(props.filters.sort_dir)
const page = ref<number>(props.filters.page)

const tab = ref<'all' | 'assessed' | 'starred' | 'contributed'>(props.category);

const lastPage = computed(() => {
    return props.items.last_page || 1;
});

const sortItems: Array<{ title: string; value: string }> = [
    {
        title: 'Name',
        value: 'name'
    },
    {
        title: 'Created date',
        value: 'created_at'
    },
    {
        title: 'Updated date',
        value: 'updated_at'
    },
    {
        title: 'Stars',
        value: 'likes_count'
    }
]

const headers: Array<DataTableHeader> = [
    {
        title: 'Name',
        value: 'name',
        sortable: false,
        align: 'start',
        width: '80%'
    },
    {
        title: 'Actions',
        value: 'actions',
        sortable: false,
        align: 'end',
        width: '20%'
    },
]

const loadItems = () => {
    const params: Record<string, string | null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
        page: page.value.toString()
    };

    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    const uri = props.category === 'all' ? route('capacity-frameworks.index') : route('capacity-frameworks.index', {category: props.category});

    router.get(
        uri,
        params,
        {
            preserveState: true,
        }
    );
}

const goToDefaultCategory = () => {
    tab.value = 'all';
    // Keep the current search and sort parameters
    const params: Record<string, string | null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
        page: '1' // Reset to the first page
    }
    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    router.get(
        route('capacity-frameworks.index'),
        params,
        {
            preserveState: true,
        }
    );
};

const goToCategory = (category: 'assessed' | 'starred' | 'contributed') => {
    tab.value = category;
    // Keep the current search and sort parameters
    const params: Record<string, string | null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
        page: '1' // Reset to the first page
    }
    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    router.get(
        route('capacity-frameworks.index', {category: category}),
        params,
        {
            preserveState: true,
        }
    );
};

const goToCapacityFramework = (id: number) => {
    router.get(route('capacity-frameworks.show', {capacityFramework: id}));
};

const createNewCapacityFramework = () => {
    router.get(route('capacity-frameworks.create'))
};

</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between">
            <h1 class="text-h4 font-weight-bold">
                {{ props.title }}
            </h1>
            <v-btn
                :class="mobile ? 'mt-3' : ''"
                :block="mobile"
                @click="createNewCapacityFramework"
            >
                Create new framework
            </v-btn>
        </v-row>
        <v-row class="mt-10 mb-0">
            <v-col
                cols="12"
                class="pa-0"
            >
                <v-tabs
                    v-model="tab"
                    align-tabs="start"
                    color="primary"
                >
                    <navigation-tab-with-chip
                        :tab="tab"
                        value="all"
                        :count="props.count"
                        @click="goToDefaultCategory"
                    >
                        All
                    </navigation-tab-with-chip>
                    <navigation-tab-with-chip
                        v-if="!isGuest"
                        :tab="tab"
                        value="assessed"
                        :count="props.assessedCount"
                        @click="goToCategory('assessed')"
                    >
                        Assessed
                    </navigation-tab-with-chip>
                    <navigation-tab-with-chip
                        :tab="tab"
                        value="starred"
                        :count="props.starredCount"
                        @click="goToCategory('starred')"
                    >
                        Starred
                    </navigation-tab-with-chip>
                    <navigation-tab-with-chip
                        tab="tab"
                        value="contributed"
                        :count="props.contributedCount"
                        @click="goToCategory('contributed')"
                    >
                        Contributed
                    </navigation-tab-with-chip>
                </v-tabs>
            </v-col>
        </v-row>
        <v-row class="mt-0 bg-grey-lighten-5 border-t-sm border-b-sm">
            <v-col
                cols="12"
                md="9"
                class="py-4"
            >
                <search-field
                    v-model="search"
                    @click="loadItems"
                />
            </v-col>
            <v-col
                cols="12"
                md="3"
                class="py-4"
            >
                <sort-field
                    v-model:sort-by="sortBy"
                    v-model:sort-dir="sortDir"
                    :items="sortItems"
                    @update:sort-by="loadItems"
                    @update:sort-dir="loadItems"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col
                cols="12"
                class="py-0"
            >
                <v-data-table
                    :headers="headers"
                    :items="props.items.data"
                    :items-per-page="props.items.per_page"
                    :page="props.items.current_page"
                    disable-sort
                    hide-default-header
                    hide-default-footer
                    no-data-text="No results found."
                >
                    <template #no-data>
                        <v-sheet class="mt-10">
                            <v-row
                                class="fill-height"
                                align="center"
                                justify="center"
                            >
                                <v-col
                                    cols="12"
                                    class="text-center"
                                >
                                    <v-avatar size="128">
                                        <v-icon
                                            size="128"
                                            color="grey-lighten-2"
                                        >
                                            mdi-emoticon-sad-outline
                                        </v-icon>
                                    </v-avatar>
                                    <h2 class="text-h5 font-weight-bold">
                                        No capacity frameworks found.
                                    </h2>
                                    <p>
                                        Try adjusting your search or category criteria.
                                    </p>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </template>
                    <template #[`item.name`]="{ item }">
                        <v-list-item>
                            <template #prepend>
                                <title-avatar
                                    :id="item.id"
                                    :title="item.name"
                                    @click="goToCapacityFramework(item.id)"
                                />
                            </template>
                            <v-list-item-title
                                class="text-body-2 font-weight-bold"
                            >
                                <span
                                    class="custom-link"
                                    @click="goToCapacityFramework(item.id)"
                                >
                                    {{ item.name }}
                                </span>
                                <v-chip
                                    variant="flat"
                                    :color="item.status === 'published' ? '#B8E2C2' : '#C0DAF8'"
                                    size="x-small"
                                    class="ml-2 px-2 text-caption float-end"
                                    :class="item.status === 'published' ? 'text-green-darken-4' : 'text-blue-darken-4'"
                                >
                                    {{ item.status === 'published' ? 'Published' : 'Draft' }}
                                </v-chip>
                                <version-chip>
                                    {{ item.version_id }}
                                </version-chip>
                            </v-list-item-title>
                        </v-list-item>
                    </template>
                    <template #[`item.actions`]="{ item }">
                        <v-row
                            v-if="!mobile"
                            no-gutters
                            justify="space-evenly"
                            dense
                        >
                            <v-col cols="3">
                                <icon-count-tooltip
                                    icon="mdi-map-marker-question-outline"
                                    :count="item.capacity_indicators_count"
                                    class="mr-2"
                                >
                                    Capacity indicators
                                </icon-count-tooltip>
                            </v-col>
                            <v-col cols="3">
                                <icon-count-tooltip
                                    icon="mdi-star-outline"
                                    :count="item.likes_count"
                                >
                                    Stars
                                </icon-count-tooltip>
                            </v-col>
                            <v-col cols="3">
                                <icon-count-tooltip
                                    icon="mdi-checkbox-multiple-marked-outline"
                                    :count="item.assessments_count"
                                >
                                    Assessments
                                </icon-count-tooltip>
                            </v-col>
                        </v-row>
                    </template>
                </v-data-table>
                <v-row
                    v-if="items.total > 0"
                    class="justify-center mt-5"
                >
                    <v-pagination
                        v-model="page"
                        :length="lastPage"
                        density="compact"
                        active-color="primary"
                        @update:model-value="loadItems"
                    />
                </v-row>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 42px;
}
</style>
